import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, Star, Phone, Mail, MapPin, ExternalLink,
  ChevronRight, Play, Quote, Check, ArrowRight, Users,
  Award, Clock, Shield
} from 'lucide-react';
import api from '../utils/api';

const PublicShop = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);

  useEffect(() => {
    fetchShop();
  }, [slug]);

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    if (shop?.id && cart.length > 0) {
      localStorage.setItem(`shop_cart_${shop.id}`, JSON.stringify(
        cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.sale_price || item.price,
          quantity: item.qty,
          image: item.images?.[0]
        }))
      ));
    }
  }, [cart, shop]);

  const fetchShop = async () => {
    try {
      const res = await api.get(`/shops/view/${slug}`);
      setShop(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Shop not found');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.id === product.id ? { ...item, qty: item.qty + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, qty: 1 }]);
    }
  };

  const goToCheckout = () => {
    if (shop?.id && cart.length > 0) {
      // Save cart first
      localStorage.setItem(`shop_cart_${shop.id}`, JSON.stringify(
        cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.sale_price || item.price,
          quantity: item.qty,
          image: item.images?.[0]
        }))
      ));
      navigate(`/shop-checkout?shop_id=${shop.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Shop Not Found</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!shop) return null;

  const colors = shop.colors || { primary: '#D4AF37', secondary: '#1a1a2e', accent: '#6366f1' };

  // Dynamic CSS variables
  const shopStyles = {
    '--shop-primary': colors.primary,
    '--shop-secondary': colors.secondary,
    '--shop-accent': colors.accent
  };

  return (
    <div className="min-h-screen" style={shopStyles}>
      {/* Render sections in order */}
      {shop.sections
        ?.filter(s => s.is_visible)
        .sort((a, b) => a.order - b.order)
        .map(section => (
          <SectionRenderer
            key={section.id}
            section={section}
            shop={shop}
            colors={colors}
            products={shop.products || []}
            onAddToCart={addToCart}
          />
        ))}

      {/* Floating Cart */}
      {cart.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={goToCheckout}
            className="p-4 rounded-full shadow-lg flex items-center gap-2 hover:scale-105 transition-transform"
            style={{ backgroundColor: colors.primary, color: '#fff' }}
            data-testid="cart-checkout-button"
          >
            <ShoppingBag className="w-6 h-6" />
            <span className="font-bold">{cart.reduce((sum, item) => sum + item.qty, 0)}</span>
            <span className="text-sm ml-1">Checkout</span>
          </button>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10">
        <p className="text-sm text-muted-foreground">
          Powered by <span style={{ color: colors.primary }}>ANANT</span>
        </p>
      </footer>
    </div>
  );
};

// Section Renderer Component
const SectionRenderer = ({ section, shop, colors, products, onAddToCart }) => {
  const content = section.content || {};

  switch (section.type) {
    case 'hero':
      return (
        <section
          className="relative min-h-[70vh] flex items-center justify-center text-center px-6"
          style={{
            background: content.background_image
              ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${content.background_image}) center/cover`
              : `linear-gradient(135deg, ${colors.secondary}, ${colors.primary}20)`
          }}
          data-testid="hero-section"
        >
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{content.title || shop.name}</h1>
            <p className="text-xl md:text-2xl text-white/80 mb-8">{content.subtitle || shop.description}</p>
            {content.cta_text && (
              <a
                href={content.cta_link || '#products'}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-transform hover:scale-105"
                style={{ backgroundColor: colors.primary, color: '#fff' }}
              >
                {content.cta_text}
                <ArrowRight className="w-5 h-5" />
              </a>
            )}
          </div>
        </section>
      );

    case 'about':
      return (
        <section className="py-20 px-6" data-testid="about-section">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            {content.image && (
              <img
                src={content.image}
                alt={content.title}
                className="w-full h-80 object-cover rounded-2xl"
              />
            )}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: colors.primary }}>
                {content.title || 'About Us'}
              </h2>
              <p className="text-lg text-white/70 leading-relaxed whitespace-pre-wrap">
                {content.text}
              </p>
            </div>
          </div>
        </section>
      );

    case 'features':
      return (
        <section className="py-20 px-6" style={{ backgroundColor: `${colors.secondary}50` }} data-testid="features-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Our Features'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {(content.items || []).map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl text-center transition-transform hover:-translate-y-2"
                  style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}30` }}
                >
                  <span className="text-4xl mb-4 block">{item.icon}</span>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-white/60">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'products':
      return (
        <section id="products" className="py-20 px-6" data-testid="products-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Our Products'}
            </h2>
            {products.length === 0 ? (
              <p className="text-center text-muted-foreground">No products available</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.filter(p => p.is_active !== false).map(product => (
                  <div
                    key={product.id}
                    className="rounded-2xl overflow-hidden transition-transform hover:-translate-y-2"
                    style={{ backgroundColor: `${colors.secondary}`, border: `1px solid ${colors.primary}30` }}
                  >
                    {product.images?.[0] && (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-1">{product.name}</h3>
                      <p className="text-sm text-white/60 line-clamp-2 mb-3">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          {product.sale_price ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xl font-bold" style={{ color: colors.primary }}>
                                ₹{product.sale_price}
                              </span>
                              <span className="text-sm line-through text-white/40">₹{product.price}</span>
                            </div>
                          ) : (
                            <span className="text-xl font-bold" style={{ color: colors.primary }}>
                              ₹{product.price}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => onAddToCart(product)}
                          className="p-2 rounded-lg transition-colors"
                          style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}
                        >
                          <ShoppingBag className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      );

    case 'testimonials':
      return (
        <section className="py-20 px-6" style={{ backgroundColor: `${colors.secondary}50` }} data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'What Our Customers Say'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(content.items || []).map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl"
                  style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}30` }}
                >
                  <Quote className="w-8 h-8 mb-4" style={{ color: colors.primary }} />
                  <p className="text-white/80 mb-4">"{item.text}"</p>
                  <div className="flex items-center gap-3">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-10 h-10 rounded-full object-cover" />
                    )}
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <div className="flex gap-1">
                        {[...Array(item.rating || 5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" style={{ color: colors.primary }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'pricing':
      return (
        <section className="py-20 px-6" data-testid="pricing-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Pricing Plans'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {(content.plans || []).map((plan, idx) => (
                <div
                  key={idx}
                  className={`p-8 rounded-2xl text-center ${plan.featured ? 'scale-105' : ''}`}
                  style={{
                    backgroundColor: plan.featured ? `${colors.primary}20` : `${colors.secondary}`,
                    border: `2px solid ${plan.featured ? colors.primary : `${colors.primary}30`}`
                  }}
                >
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-4xl font-bold mb-4" style={{ color: colors.primary }}>
                    ₹{plan.price}<span className="text-sm text-white/60">/mo</span>
                  </p>
                  <ul className="space-y-3 mb-6 text-left">
                    {(plan.features || []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="w-5 h-5" style={{ color: colors.primary }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className="w-full py-3 rounded-full font-semibold"
                    style={{ backgroundColor: colors.primary, color: '#fff' }}
                  >
                    Get Started
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'contact':
      return (
        <section className="py-20 px-6" style={{ backgroundColor: `${colors.secondary}50` }} data-testid="contact-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Contact Us'}
            </h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                {content.email && (
                  <div className="flex items-center gap-4">
                    <Mail className="w-6 h-6" style={{ color: colors.primary }} />
                    <div>
                      <p className="text-sm text-white/60">Email</p>
                      <a href={`mailto:${content.email}`} className="font-semibold">{content.email}</a>
                    </div>
                  </div>
                )}
                {content.phone && (
                  <div className="flex items-center gap-4">
                    <Phone className="w-6 h-6" style={{ color: colors.primary }} />
                    <div>
                      <p className="text-sm text-white/60">Phone</p>
                      <a href={`tel:${content.phone}`} className="font-semibold">{content.phone}</a>
                    </div>
                  </div>
                )}
                {content.address && (
                  <div className="flex items-center gap-4">
                    <MapPin className="w-6 h-6" style={{ color: colors.primary }} />
                    <div>
                      <p className="text-sm text-white/60">Address</p>
                      <p className="font-semibold">{content.address}</p>
                    </div>
                  </div>
                )}
              </div>
              <form className="space-y-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10"
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10"
                />
                <textarea
                  placeholder="Your Message"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10"
                />
                <button
                  type="submit"
                  className="w-full py-3 rounded-full font-semibold"
                  style={{ backgroundColor: colors.primary, color: '#fff' }}
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </section>
      );

    case 'cta':
      return (
        <section
          className="py-20 px-6 text-center"
          style={{ background: `linear-gradient(135deg, ${colors.primary}20, ${colors.accent}20)` }}
          data-testid="cta-section"
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{content.title || 'Ready to Get Started?'}</h2>
            <p className="text-xl text-white/70 mb-8">{content.subtitle}</p>
            {content.button_text && (
              <a
                href={content.button_link || '#'}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg"
                style={{ backgroundColor: colors.primary, color: '#fff' }}
              >
                {content.button_text}
                <ArrowRight className="w-5 h-5" />
              </a>
            )}
          </div>
        </section>
      );

    case 'team':
      return (
        <section className="py-20 px-6" data-testid="team-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Our Team'}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {(content.members || []).map((member, idx) => (
                <div key={idx} className="text-center">
                  {member.image && (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                    />
                  )}
                  <h3 className="font-bold text-lg">{member.name}</h3>
                  <p className="text-sm" style={{ color: colors.primary }}>{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'stats':
      return (
        <section
          className="py-20 px-6"
          style={{ backgroundColor: `${colors.primary}10` }}
          data-testid="stats-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {(content.stats || [
                { value: '1000+', label: 'Happy Customers', icon: Users },
                { value: '500+', label: 'Products Sold', icon: ShoppingBag },
                { value: '99%', label: 'Satisfaction', icon: Award },
                { value: '24/7', label: 'Support', icon: Clock }
              ]).map((stat, idx) => {
                const Icon = stat.icon || Star;
                return (
                  <div key={idx}>
                    <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: colors.primary }}>
                      {stat.value}
                    </p>
                    <p className="text-white/60">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      );

    case 'faq':
      return (
        <section className="py-20 px-6" data-testid="faq-section">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Frequently Asked Questions'}
            </h2>
            <div className="space-y-4">
              {(content.items || []).map((item, idx) => (
                <FaqItem key={idx} question={item.question} answer={item.answer} colors={colors} />
              ))}
            </div>
          </div>
        </section>
      );

    case 'video':
      return (
        <section className="py-20 px-6" data-testid="video-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8" style={{ color: colors.primary }}>
              {content.title || 'Watch Our Video'}
            </h2>
            {content.url && (
              <div className="aspect-video rounded-2xl overflow-hidden">
                <iframe
                  src={content.url}
                  className="w-full h-full"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        </section>
      );

    case 'gallery':
      return (
        <section className="py-20 px-6" data-testid="gallery-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: colors.primary }}>
              {content.title || 'Gallery'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(content.images || []).map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-48 object-cover rounded-xl"
                />
              ))}
            </div>
          </div>
        </section>
      );

    case 'text':
      return (
        <section className="py-16 px-6" data-testid="text-section">
          <div className="max-w-3xl mx-auto">
            {content.title && (
              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: colors.primary }}>
                {content.title}
              </h2>
            )}
            <div className="prose prose-invert max-w-none text-white/80 whitespace-pre-wrap">
              {content.text}
            </div>
          </div>
        </section>
      );

    case 'custom_html':
      return (
        <section className="py-16 px-6" data-testid="custom-html-section">
          <div
            className="max-w-6xl mx-auto"
            dangerouslySetInnerHTML={{ __html: content.html || '' }}
          />
        </section>
      );

    default:
      return null;
  }
};

// FAQ Item Component
const FaqItem = ({ question, answer, colors }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}30` }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <span className="font-semibold">{question}</span>
        <ChevronRight className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-white/70">
          {answer}
        </div>
      )}
    </div>
  );
};

export default PublicShop;
